//src/components/gro/RiskInventoryAiInsightsPanel.tsx
import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Sparkles, ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { PaginationFooter } from "@/components/shared/PaginationFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useGenerateRiskInventoryAiInsights, useRiskInventoryAiInsights, useUpdateRiskInventoryAiInsightFeedback } from "@/hooks/useRiskInventories";
import type { AiFeedbackStatus, AiGeneratedOutput } from "@/types/ai";

function formatCurrency(value?: string | number | null, currency = "BRL") {
  const n = Number(value || 0);
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(Number.isFinite(n) ? n : 0);
}

function formatInteger(value?: string | number | null) {
  const n = Number(value || 0);
  return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(Number.isFinite(n) ? n : 0);
}

function formatDateTime(value?: string | null) {
  if (!value) return "-";
  try {
    const date = new Date(value);
    const day = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(date);
    const time = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(date);
    return `${day} às ${time}`;
  } catch {
    return value;
  }
}

function statusLabel(status?: string | null) {
  if (status === "failed") return "Falha na geração";
  if (status === "generated") return "Gerado";
  return status || "Gerado";
}


function FeedbackButtons({
  value,
  onChange,
  disabled,
}: {
  value?: AiFeedbackStatus;
  onChange: (value: AiFeedbackStatus) => void;
  disabled?: boolean;
}) {
  const current = value ?? null;
  const nextLike = current === "like" ? null : "like";
  const nextUnlike = current === "unlike" ? null : "unlike";

  return (
    <div className="flex items-center gap-1" aria-label="Avaliação do insight Normex Copilot">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={`h-8 w-8 rounded-full ${current === "like" ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700" : "text-slate-400 hover:text-emerald-600"}`}
        onClick={() => onChange(nextLike)}
        disabled={disabled}
        title="Marcar como útil"
        aria-label="Marcar como útil"
      >
        <ThumbsUp className={`h-4 w-4 ${current === "like" ? "fill-current" : ""}`} />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={`h-8 w-8 rounded-full ${current === "unlike" ? "bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700" : "text-slate-400 hover:text-red-600"}`}
        onClick={() => onChange(nextUnlike)}
        disabled={disabled}
        title="Marcar como não útil"
        aria-label="Marcar como não útil"
      >
        <ThumbsDown className={`h-4 w-4 ${current === "unlike" ? "fill-current" : ""}`} />
      </Button>
    </div>
  );
}

function InsightDetail({ item }: { item: AiGeneratedOutput }) {
  const output = item.output || {};
  return (
    <div className="space-y-4 border-t p-4">
      <Card className="border-blue-100 bg-blue-50 p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">1. Resumo executivo</div>
        <p className="mt-2 text-sm text-blue-950">{output.executive_summary || "Resumo executivo não informado pelo Normex Copilot."}</p>
      </Card>

      {(output.key_findings || []).length ? (
        <Card className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">2. Principais achados</div>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {output.key_findings?.map((value, index) => <li key={index}>{value}</li>)}
          </ul>
        </Card>
      ) : null}

      {(output.hidden_bottlenecks || []).length ? (
        <Card className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">3. Gargalos ocultos</div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {output.hidden_bottlenecks?.map((item, index) => (
              <div key={`${item.title}-${index}`} className="rounded-xl border bg-slate-50 p-3 text-sm">
                <div className="font-medium text-slate-950">{item.title}</div>
                <p className="mt-1 text-muted-foreground">{item.description}</p>
                <p className="mt-2 text-xs text-slate-600"><strong>Impacto:</strong> {item.impact}</p>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {(output.management_priorities || []).length ? (
        <Card className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-purple-700">4. Prioridades de gestão</div>
          <div className="mt-3 space-y-2">
            {output.management_priorities?.map((item, index) => (
              <div key={`${item.title}-${index}`} className="rounded-xl border bg-white p-3 text-sm">
                <div className="font-medium text-slate-950">{item.priority || index + 1}. {item.title}</div>
                <p className="mt-1 text-muted-foreground">{item.reason}</p>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {(output.strategic_recommendations || []).length ? (
        <Card className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">5. Recomendações estratégicas</div>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {output.strategic_recommendations?.map((value, index) => <li key={index}>{value}</li>)}
          </ul>
        </Card>
      ) : null}

      {(output.attention_points || []).length ? (
        <Card className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-red-700">6. Pontos de atenção</div>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {output.attention_points?.map((value, index) => <li key={index}>{value}</li>)}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

export function RiskInventoryAiInsightsPanel({ inventoryId, canGenerate }: { inventoryId?: string; canGenerate: boolean }) {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [lastGeneratedInsight, setLastGeneratedInsight] = useState<AiGeneratedOutput | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const insightsQ = useRiskInventoryAiInsights(inventoryId, { page, per_page: 10 });
  const generateMut = useGenerateRiskInventoryAiInsights();
  const feedbackMut = useUpdateRiskInventoryAiInsightFeedback();
  const dataBase = insightsQ.data ?? { items: [], total: 0, page: 1, per_page: 10, total_pages: 1 };
  const data = useMemo(() => {
    if (!lastGeneratedInsight) return dataBase;
    const alreadyListed = dataBase.items.some((item) => item.id === lastGeneratedInsight.id);
    if (alreadyListed) return dataBase;
    return {
      ...dataBase,
      items: [lastGeneratedInsight, ...dataBase.items],
      total: dataBase.total + 1,
      total_pages: Math.max(dataBase.total_pages, 1),
    };
  }, [dataBase, lastGeneratedInsight]);
  const hasPrevious = data.total > 0;

  async function confirmGenerate() {
    if (!inventoryId) return;
    try {
      const result = await generateMut.mutateAsync({ inventoryId });
      setLastGeneratedInsight(result);
      setPage(1);
      setExpandedId(result.id);
      setConfirmOpen(false);
      await insightsQ.refetch();
      toast.success("Insights gerados com sucesso.");
    } catch (error: any) {
      toast.error(error?.message || "Erro ao gerar insights com Normex Copilot.");
    }
  }



  async function handleFeedback(outputId: string, feedbackStatus: AiFeedbackStatus) {
    if (!inventoryId) return;
    try {
      await feedbackMut.mutateAsync({ inventoryId, outputId, feedbackStatus });
      await insightsQ.refetch();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao registrar avaliação do insight Normex Copilot.");
    }
  }

  return (
    <Card className="p-4">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="text-sm font-semibold">Insights Normex Copilot</div>
          <div className="text-sm text-muted-foreground">
            Gere e consulte análises executivas do inventário sem criar riscos, planos ou ações.
          </div>
        </div>
        {canGenerate ? (
          <Button variant="outline" onClick={() => setConfirmOpen(true)} disabled={generateMut.isPending}>
            <Sparkles className={`mr-2 h-4 w-4 ${generateMut.isPending ? "animate-pulse" : ""}`} />
            Gerar insights com Normex Copilot
          </Button>
        ) : null}
      </div>

      {generateMut.isPending ? (
        <Card className="mb-4 border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-blue-200 border-t-blue-700" aria-hidden="true" />
            <div>
              <div className="font-medium">Gerando insights com Normex Copilot...</div>
              <div className="mt-1 text-blue-800">Analisando riscos aprovados, planos de ação, ações em andamento, atrasos e gargalos do inventário.</div>
            </div>
          </div>
        </Card>
      ) : null}

      {insightsQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">Carregando histórico de insights Normex Copilot...</Card>
      ) : insightsQ.isError ? (
        <Card className="p-6 text-sm text-destructive">{(insightsQ.error as any)?.message || "Erro ao carregar insights Normex Copilot."}</Card>
      ) : data.items.length === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground">Ainda não há insights Normex Copilot para este inventário.</Card>
      ) : (
        <div className="space-y-4">
          {data.items.map((item) => (
            <Card key={item.id} className="overflow-hidden border-slate-200">
              <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-semibold">Insight Normex Copilot - {formatDateTime(item.created_at)}</div>
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">{statusLabel(item.status)}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">{item.output?.executive_summary || "Insight gerado e salvo no histórico do inventário."}</div>
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span>•</span>
                    <span>Achados: {item.output?.key_findings?.length || 0}</span>
                    <span>•</span>
                    <span>Gargalos: {item.output?.hidden_bottlenecks?.length || 0}</span>
                  </div>
                </div>
                <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-3 lg:min-w-[420px]">
                  <div className="rounded-lg border bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wide">Tokens de entrada</div>
                    <div className="text-sm font-semibold text-slate-900">{formatInteger(item.usage?.input_tokens)}</div>
                  </div>
                  <div className="rounded-lg border bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wide">Tokens de saída</div>
                    <div className="text-sm font-semibold text-slate-900">{formatInteger(item.usage?.output_tokens)}</div>
                  </div>
                  <div className="rounded-lg border bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wide">Valor debitado</div>
                    <div className="text-sm font-semibold text-slate-900">{formatCurrency(item.usage?.charged_amount_brl)}</div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between border-t bg-slate-50 px-4 py-3">
                <div className="text-xs text-muted-foreground">Consultar este histórico não consome créditos.</div>
                <div className="flex items-center gap-2">
                  <FeedbackButtons value={item.feedback_status ?? null} onChange={(value) => handleFeedback(item.id, value)} disabled={feedbackMut.isPending} />
                  <Button variant="outline" size="sm" onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}>
                    {expandedId === item.id ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
                    Ver detalhes
                  </Button>
                </div>
              </div>
              {expandedId === item.id ? <InsightDetail item={item} /> : null}
            </Card>
          ))}
          <PaginationFooter
            page={data.page}
            perPage={data.per_page as 10 | 20 | 30}
            total={data.total}
            totalPages={data.total_pages}
            onPageChange={setPage}
            onPerPageChange={() => setPage(1)}
            label="insights"
          />
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={hasPrevious ? "Gerar novo insight com Normex Copilot?" : "Gerar insights com Normex Copilot?"}
        description={hasPrevious
          ? "Este inventário já possui insights Normex Copilot gerados anteriormente. Você pode consultar o histórico sem consumir créditos. Deseja gerar uma nova análise mesmo assim?"
          : "O Normex Copilot irá analisar riscos, planos de ação e ações vinculados a este inventário para gerar uma visão executiva de gestão. Esta ação consumirá créditos Normex Copilot. Deseja continuar?"}
        onConfirm={confirmGenerate}
        confirmLabel={hasPrevious ? "Gerar nova análise" : "Gerar insights"}
      />
    </Card>
  );
}
